import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminLogin,
  getAdminDashboard,
  getAdminReservations,
  getAdminBottles,
  updateBottleCount,
  adminConfirmBottle,
  adminReleaseBottle,
  adminUpdateUser,
  adminAssignBottle,
  adminSetMaxBottles,
  getAdminExport,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users,
  Wine,
  CheckCircle,
  Clock,
  Download,
  LogOut,
  Settings,
  Search,
  RefreshCw,
  Pencil,
  X,
  Plus,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solaro-founding/artifacts/6pe0ovti_Solaro%20Capri%20Gin%20-%20Horizontal%20Vectorial%20Handpainted%20PNG%20TRANSPARENT.png";

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem("solaro_admin_token", res.data.token);
      onLogin(res.data.token);
      toast.success("Admin access granted");
    } catch {
      toast.error("Invalid admin password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Solaro" className="h-12 mx-auto mb-6 object-contain" />
          <h1 className="font-display text-2xl text-[#002366] tracking-tight">Admin Panel</h1>
          <p className="font-body text-sm text-[#1A2B4C]/50 mt-1">Enter admin password to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white p-6 shadow-lg space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
              Password
            </Label>
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              required
              className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
            />
          </div>
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-5"
          >
            {loading ? "Verifying..." : "Access Admin"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <Icon size={18} className={color} />
        <span className="text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body">{label}</span>
      </div>
      <span className="font-display text-3xl text-[#002366]">{value}</span>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("solaro_admin_token"));
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [bottlesData, setBottlesData] = useState({ bottles: [], total_bottles: 300 });
  const [newTotal, setNewTotal] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", whatsapp: "", address: "" });
  const [assignModal, setAssignModal] = useState(null); // { user_id, user_name }
  const [assignBottleNum, setAssignBottleNum] = useState("");
  const [maxBottlesModal, setMaxBottlesModal] = useState(null); // { user_id, user_name, current_max }
  const [maxBottlesValue, setMaxBottlesValue] = useState("");
  const navigate = useNavigate();

  // Set admin token on api requests
  useEffect(() => {
    if (token) {
      localStorage.setItem("solaro_admin_token", token);
    }
  }, [token]);

  // Override API token for admin
  const adminApi = useCallback(
    async (apiFn) => {
      const prevToken = localStorage.getItem("solaro_token");
      localStorage.setItem("solaro_token", token);
      try {
        const result = await apiFn();
        return result;
      } finally {
        if (prevToken) {
          localStorage.setItem("solaro_token", prevToken);
        } else {
          localStorage.removeItem("solaro_token");
        }
      }
    },
    [token]
  );

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminApi(getAdminDashboard);
      setDashboard(res.data);
    } catch {
      toast.error("Failed to load dashboard");
      setToken(null);
      localStorage.removeItem("solaro_admin_token");
    }
  }, [adminApi]);

  const fetchReservations = useCallback(async () => {
    try {
      const res = await adminApi(getAdminReservations);
      setReservations(res.data);
    } catch {
      toast.error("Failed to load reservations");
    }
  }, [adminApi]);

  const fetchBottles = useCallback(async () => {
    try {
      const res = await adminApi(getAdminBottles);
      setBottlesData(res.data);
    } catch {
      toast.error("Failed to load bottles");
    }
  }, [adminApi]);

  useEffect(() => {
    if (token) {
      fetchDashboard();
    }
  }, [token, fetchDashboard]);

  useEffect(() => {
    if (token && activeTab === "reservations") fetchReservations();
    if (token && activeTab === "bottles") fetchBottles();
  }, [token, activeTab, fetchReservations, fetchBottles]);

  const handleUpdateCount = async () => {
    const num = parseInt(newTotal, 10);
    if (!num || num < 1) {
      toast.error("Enter a valid number");
      return;
    }
    setLoading(true);
    try {
      await adminApi(() => updateBottleCount(num));
      toast.success(`Total bottles updated to ${num}`);
      setNewTotal("");
      fetchDashboard();
      fetchBottles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setLoading(false);
  };

  const handleConfirm = async (number) => {
    try {
      await adminApi(() => adminConfirmBottle(number));
      toast.success(`Bottle #${number} confirmed`);
      fetchBottles();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleRelease = async (number) => {
    try {
      await adminApi(() => adminReleaseBottle(number));
      toast.success(`Bottle #${number} released`);
      fetchBottles();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleExport = async () => {
    try {
      const res = await adminApi(getAdminExport);
      const data = res.data;
      const headers = ["Name", "Email", "WhatsApp", "Confirmed Bottles", "Held Bottles", "Total Bottles", "Registered At"];
      const rows = data.map((r) => [
        r.name,
        r.email,
        r.whatsapp || "",
        r.confirmed_bottles,
        r.held_bottles,
        r.total_bottles,
        r.registered_at,
      ]);
      const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "solaro_founding_members.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("solaro_admin_token");
  };

  const openEditUser = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      whatsapp: user.whatsapp || "",
      address: user.address || "",
    });
  };

  const handleSaveUser = async () => {
    setLoading(true);
    try {
      await adminApi(() =>
        adminUpdateUser({
          user_id: editUser.id,
          name: editForm.name,
          email: editForm.email,
          whatsapp: editForm.whatsapp,
          address: editForm.address,
        })
      );
      toast.success(`${editForm.name} updated`);
      setEditUser(null);
      fetchReservations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setLoading(false);
  };

  const handleAssignBottle = async () => {
    const num = parseInt(assignBottleNum, 10);
    if (!num || num < 1) {
      toast.error("Enter a valid bottle number");
      return;
    }
    setLoading(true);
    try {
      await adminApi(() => adminAssignBottle(assignModal.user_id, num));
      toast.success(`Bottle #${num} assigned to ${assignModal.user_name}`);
      setAssignModal(null);
      setAssignBottleNum("");
      fetchReservations();
      fetchBottles();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
    setLoading(false);
  };

  const handleSaveMaxBottles = async () => {
    const num = parseInt(maxBottlesValue, 10);
    if (!num || num < 1) {
      toast.error("Enter a valid number (minimum 1)");
      return;
    }
    setLoading(true);
    try {
      await adminApi(() => adminSetMaxBottles(maxBottlesModal.user_id, num));
      toast.success(`Max bottles for ${maxBottlesModal.user_name} set to ${num}`);
      setMaxBottlesModal(null);
      setMaxBottlesValue("");
      fetchReservations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setLoading(false);
  };

  if (!token) {
    return <AdminLogin onLogin={setToken} />;
  }

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "reservations", label: "Members" },
    { id: "bottles", label: "Bottles" },
  ];

  const filteredReservations = reservations.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredBottles = bottlesData.bottles.filter(
    (b) => b.status !== "available"
  );

  return (
    <div className="min-h-screen" data-testid="admin-page">
      {/* Header */}
      <header className="bg-[#002366] text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Solaro" className="h-8 object-contain brightness-0 invert" />
            <span className="font-body text-xs uppercase tracking-widest text-white/60">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-view-site-btn"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-widest text-white/60 hover:text-white font-body transition-colors"
            >
              View Site
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="p-2 text-white/60 hover:text-white transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-[#D1CFC7] px-3 sm:px-4 overflow-x-auto">
        <div className="max-w-7xl mx-auto flex gap-0 min-w-max sm:min-w-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-xs uppercase tracking-widest font-body border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-[#D4AF37] text-[#002366]"
                  : "border-transparent text-[#1A2B4C]/40 hover:text-[#002366]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* DASHBOARD TAB */}
        {activeTab === "dashboard" && dashboard && (
          <div className="animate-fade-in-up space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-[#002366]">Overview</h2>
              <Button
                data-testid="admin-refresh-btn"
                onClick={fetchDashboard}
                variant="outline"
                className="border-[#D1CFC7] text-[#002366] rounded-none text-xs uppercase tracking-widest px-4 py-2"
              >
                <RefreshCw size={14} className="mr-2" /> Refresh
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              <StatCard icon={Wine} label="Total Bottles" value={dashboard.total_bottles} color="text-[#002366]" />
              <StatCard icon={Users} label="Members" value={dashboard.total_users} color="text-[#D4AF37]" />
              <StatCard icon={Clock} label="Held" value={dashboard.total_held} color="text-amber-500" />
              <StatCard icon={CheckCircle} label="Confirmed" value={dashboard.total_confirmed} color="text-emerald-600" />
              <StatCard icon={Wine} label="Available" value={dashboard.total_available} color="text-[#002366]/50" />
            </div>

            {/* Bottle count setting */}
            <div className="bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Settings size={18} className="text-[#002366]" />
                <h3 className="font-display text-lg text-[#002366]">Bottle Pool Settings</h3>
              </div>
              <p className="font-body text-sm text-[#1A2B4C]/50 mb-4">
                Current pool: <strong>{dashboard.total_bottles}</strong> bottles. Changing this will adjust how many bottles are visible and available to users.
              </p>
              <div className="flex gap-3 items-end">
                <div className="flex-1 max-w-xs">
                  <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                    New Total
                  </Label>
                  <Input
                    data-testid="admin-bottle-count-input"
                    type="number"
                    min={1}
                    max={1000}
                    value={newTotal}
                    onChange={(e) => setNewTotal(e.target.value)}
                    placeholder={String(dashboard.total_bottles)}
                    className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
                  />
                </div>
                <Button
                  data-testid="admin-update-count-btn"
                  onClick={handleUpdateCount}
                  disabled={loading}
                  className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs px-6 py-5"
                >
                  Update
                </Button>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex gap-3">
              <Button
                data-testid="admin-export-btn"
                onClick={handleExport}
                variant="outline"
                className="border-[#002366] text-[#002366] hover:bg-[#002366] hover:text-white rounded-none uppercase tracking-widest text-xs px-6 py-4"
              >
                <Download size={14} className="mr-2" /> Export Members CSV
              </Button>
            </div>
          </div>
        )}

        {/* RESERVATIONS TAB */}
        {activeTab === "reservations" && (
          <div className="animate-fade-in-up space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-2xl text-[#002366]">Founding Members</h2>
              <div className="flex items-center gap-2 bg-white border border-[#D1CFC7] px-3 py-2">
                <Search size={14} className="text-[#1A2B4C]/30" />
                <input
                  data-testid="admin-search-input"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="border-none outline-none font-body text-sm bg-transparent w-48"
                />
              </div>
            </div>

            <div className="bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-reservations-table">
                  <thead>
                    <tr className="border-b border-[#D1CFC7]">
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Name</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Email</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">WhatsApp</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Bottles</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Registered</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReservations.map((u) => (
                      <tr key={u.id} className="border-b border-[#D1CFC7]/30 hover:bg-[#F9F7F2]/50">
                        <td className="px-4 py-3 font-body text-sm text-[#002366]">{u.name}</td>
                        <td className="px-4 py-3 font-body text-sm text-[#1A2B4C]/70">{u.email}</td>
                        <td className="px-4 py-3 font-body text-sm text-[#1A2B4C]/50">{u.whatsapp || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {u.bottles?.map((b) => (
                              <Badge
                                key={b.number}
                                className={`text-xs border-none ${
                                  b.status === "confirmed"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                #{b.number}
                              </Badge>
                            ))}
                            {(!u.bottles || u.bottles.length === 0) && (
                              <span className="text-xs text-[#1A2B4C]/30">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-body text-xs text-[#1A2B4C]/40">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              data-testid={`admin-edit-user-${u.id}`}
                              onClick={() => openEditUser(u)}
                              className="p-1.5 text-[#002366]/50 hover:text-[#002366] transition-colors"
                              title="Edit user"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              data-testid={`admin-assign-bottle-${u.id}`}
                              onClick={() => { setAssignModal({ user_id: u.id, user_name: u.name }); setAssignBottleNum(""); fetchBottles(); }}
                              className="p-1.5 text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors"
                              title="Assign bottle"
                            >
                              <Plus size={14} />
                            </button>
                            <button
                              data-testid={`admin-max-bottles-${u.id}`}
                              onClick={() => { setMaxBottlesModal({ user_id: u.id, user_name: u.name, current_max: u.max_bottles || 6 }); setMaxBottlesValue(String(u.max_bottles || 6)); }}
                              className="p-1.5 text-[#002366]/40 hover:text-[#002366] transition-colors relative"
                              title={`Max bottles: ${u.max_bottles || 6}`}
                            >
                              <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 1h8v3c2 1 3 3 3 5v7c0 2-2 3-4 3H5c-2 0-4-1-4-3v-7c0-2 1-4 3-5V1z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                <text x="8" y="14" textAnchor="middle" fontSize="8" fontWeight="bold" fill="currentColor">{u.max_bottles || 6}</text>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredReservations.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center font-body text-sm text-[#1A2B4C]/40">
                          No members found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* BOTTLES TAB */}
        {activeTab === "bottles" && (
          <div className="animate-fade-in-up space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-[#002366]">
                Active Bottles ({filteredBottles.length} held/confirmed)
              </h2>
              <Button
                data-testid="admin-refresh-bottles-btn"
                onClick={fetchBottles}
                variant="outline"
                className="border-[#D1CFC7] text-[#002366] rounded-none text-xs uppercase tracking-widest px-4 py-2"
              >
                <RefreshCw size={14} className="mr-2" /> Refresh
              </Button>
            </div>

            <div className="bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-bottles-table">
                  <thead>
                    <tr className="border-b border-[#D1CFC7]">
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Number</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Status</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Held By</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-[#1A2B4C]/40 font-body font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBottles.map((b) => (
                      <tr key={b.number} className="border-b border-[#D1CFC7]/30 hover:bg-[#F9F7F2]/50">
                        <td className="px-4 py-3 font-display text-lg text-[#002366]">#{b.number}</td>
                        <td className="px-4 py-3">
                          <Badge
                            className={`text-xs border-none ${
                              b.status === "confirmed"
                                ? "bg-emerald-100 text-emerald-700"
                                : b.status === "held"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {b.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-body text-sm text-[#1A2B4C]/70">{b.holder_name || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {b.status === "held" && (
                              <button
                                data-testid={`admin-confirm-${b.number}`}
                                onClick={() => handleConfirm(b.number)}
                                className="text-xs uppercase tracking-widest text-emerald-600 hover:text-emerald-800 font-body transition-colors"
                              >
                                Confirm
                              </button>
                            )}
                            {(b.status === "held" || b.status === "confirmed") && (
                              <button
                                data-testid={`admin-release-${b.number}`}
                                onClick={() => handleRelease(b.number)}
                                className="text-xs uppercase tracking-widest text-[#EA580C] hover:text-red-800 font-body transition-colors"
                              >
                                Release
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredBottles.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center font-body text-sm text-[#1A2B4C]/40">
                          No active bottles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="edit-user-modal">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setEditUser(null)} />
          <div className="relative w-full max-w-md bg-white shadow-2xl p-6 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg text-[#002366]">Edit Member</h2>
              <button onClick={() => setEditUser(null)} className="p-1 text-[#1A2B4C]/40 hover:text-[#002366]"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-1.5 block">Name</Label>
                <Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-1.5 block">Email</Label>
                <Input data-testid="edit-user-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-1.5 block">WhatsApp</Label>
                <Input data-testid="edit-user-whatsapp" value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} placeholder="+39 123 456 7890" className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-1.5 block">Address</Label>
                <Input data-testid="edit-user-address" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} placeholder="Street, City, Country" className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button data-testid="edit-user-save-btn" onClick={handleSaveUser} disabled={loading} className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs px-6 py-4">{loading ? "Saving..." : "Save"}</Button>
              <Button onClick={() => setEditUser(null)} variant="outline" className="border-[#D1CFC7] text-[#1A2B4C]/60 rounded-none uppercase tracking-widest text-xs px-6 py-4">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Bottle Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="assign-bottle-modal">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setAssignModal(null)} />
          <div className="relative w-full max-w-sm bg-white shadow-2xl p-6 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg text-[#002366]">Assign Bottle</h2>
              <button onClick={() => setAssignModal(null)} className="p-1 text-[#1A2B4C]/40 hover:text-[#002366]"><X size={18} /></button>
            </div>
            <p className="font-body text-sm text-[#1A2B4C]/60 mb-4">
              Assign a specific bottle to <strong className="text-[#002366]">{assignModal.user_name}</strong>. Specific selection at <strong className="text-[#D4AF37]">&#8364;50</strong>.
            </p>
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-1.5 block">Bottle Number</Label>
              <Input data-testid="assign-bottle-number" type="number" min={1} max={bottlesData.total_bottles} value={assignBottleNum} onChange={(e) => setAssignBottleNum(e.target.value)} placeholder={`1 — ${bottlesData.total_bottles}`} className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              {(() => {
                const num = parseInt(assignBottleNum, 10);
                if (!assignBottleNum || isNaN(num)) return null;
                if (num < 1 || num > bottlesData.total_bottles) return (
                  <p className="font-body text-xs text-red-500 mt-1.5" data-testid="assign-bottle-error">Number must be between 1 and {bottlesData.total_bottles}</p>
                );
                const taken = bottlesData.bottles.find(b => b.number === num && b.status !== 'available');
                if (taken) return (
                  <p className="font-body text-xs text-red-500 mt-1.5" data-testid="assign-bottle-taken">Bottle #{num} is already {taken.status} by <strong>{taken.holder_name || 'another user'}</strong></p>
                );
                return <p className="font-body text-xs text-emerald-600 mt-1.5" data-testid="assign-bottle-available">Bottle #{num} is available</p>;
              })()}
            </div>
            <div className="flex gap-3">
              {(() => {
                const num = parseInt(assignBottleNum, 10);
                const isValid = !isNaN(num) && num >= 1 && num <= bottlesData.total_bottles;
                const isTaken = isValid && bottlesData.bottles.some(b => b.number === num && b.status !== 'available');
                const canAssign = isValid && !isTaken && !loading;
                return (
                  <Button data-testid="assign-bottle-confirm-btn" onClick={handleAssignBottle} disabled={!canAssign}
                    className={`rounded-none uppercase tracking-widest text-xs px-6 py-4 ${canAssign ? 'bg-[#002366] text-white hover:bg-[#001133]' : 'bg-[#D1CFC7] text-[#1A2B4C]/30 cursor-not-allowed'}`}>
                    {loading ? "Assigning..." : "Assign"}
                  </Button>
                );
              })()}
              <Button onClick={() => setAssignModal(null)} variant="outline" className="border-[#D1CFC7] text-[#1A2B4C]/60 rounded-none uppercase tracking-widest text-xs px-6 py-4">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Max Bottles Modal */}
      {maxBottlesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="max-bottles-modal">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMaxBottlesModal(null)} />
          <div className="relative w-full max-w-sm bg-white shadow-2xl p-6 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg text-[#002366]">Max Bottles Limit</h2>
              <button onClick={() => setMaxBottlesModal(null)} className="p-1 text-[#1A2B4C]/40 hover:text-[#002366]"><X size={18} /></button>
            </div>
            <p className="font-body text-sm text-[#1A2B4C]/60 mb-4">
              Set the maximum number of bottles allowed for <strong className="text-[#002366]">{maxBottlesModal.user_name}</strong>.
              <br />
              <span className="text-xs text-[#1A2B4C]/40">Current limit: <strong>{maxBottlesModal.current_max}</strong></span>
            </p>
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-1.5 block">Max Bottles</Label>
              <Input
                data-testid="max-bottles-input"
                type="number"
                min={1}
                max={300}
                value={maxBottlesValue}
                onChange={(e) => setMaxBottlesValue(e.target.value)}
                placeholder="e.g. 6"
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="max-bottles-save-btn"
                onClick={handleSaveMaxBottles}
                disabled={loading || !maxBottlesValue || parseInt(maxBottlesValue, 10) < 1}
                className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-xs px-6 py-4"
              >
                {loading ? "Saving..." : "Save Limit"}
              </Button>
              <Button onClick={() => setMaxBottlesModal(null)} variant="outline" className="border-[#D1CFC7] text-[#1A2B4C]/60 rounded-none uppercase tracking-widest text-xs px-6 py-4">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
